import { ArrowLeftIcon, EyeIcon, ListPlusIcon, Loader2Icon, PlusIcon, SaveIcon, Trash2Icon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { ChosenCustomer, CustomerPicker, ItemPicker } from "@/features/invoices/DocumentPickers"
import { DocumentDiscountField, LineDiscountField } from "@/features/invoices/DiscountField"
import { DraftConflictAlert } from "@/features/drafts/DraftConflictAlert"
import { DraftNameBadge } from "@/features/drafts/DraftNameBadge"
import { DraftReadyControl } from "@/features/drafts/DraftReadyControl"
import {
  adoptedLines,
  discountInput,
  emptyLine,
  linesFromInvoice,
  makeKeyFactory,
  toApiBody,
  typedDiscount,
  useBillableChallans,
  useCreateInvoice,
  useCustomerById,
  useInvoicePreview,
  type EditorLine,
  type EditorState,
} from "@/features/invoices/use-invoice-editor"
import { useInvoice } from "@/features/invoices/use-invoice"
import { useDraft, useDraftAutosave } from "@/features/drafts/use-document-draft"
import { useAuth } from "@/features/auth/auth-context"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DateField } from "@/components/DateField"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"
import { requestBlob } from "@/lib/api"
import { ApiError } from "@/lib/api-error"
import { todayInputValue, useFormatDay } from "@/lib/dates"
import {
  endpoints,
  type ChallanListRow,
  type Customer,
  type DocumentTotals,
} from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { basisPointsToInputValue } from "@/lib/units"
import { routes } from "@/routes/paths"

/**
 * Making a bill (§7, §11.1, §11.3).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE MOST CONSEQUENTIAL SCREEN IN THE APP, because of what happens after Save.
 * An issued invoice cannot be edited — `invoice.edit` does not exist as a
 * capability for anyone (§2.1) — and it cannot be deleted, only cancelled with
 * its number retained for ever (§6). So the moment BEFORE Save is the last one
 * at which a mistake is free, and this screen's whole job is to make that moment
 * informative.
 *
 * EVERY FIGURE COMES FROM THE SERVER. The line amounts, the tax split, the
 * round-off and the grand total are all read from `POST /invoices/preview`,
 * which runs the same pipeline the save runs (§6). Nothing on this screen
 * multiplies a rate by a quantity. A client-side "quick preview" would be a
 * second tax engine, and CLAUDE.md forbids it for the reason that matters: the
 * day the two disagreed, the customer would find out before we did.
 *
 * THE NUMBER IS NOT SHOWN, because it does not exist yet. It is allocated inside
 * the save transaction so the series stays gap-free (§6); showing a predicted
 * one would be wrong the moment two bills are started at once.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * The units the real documents use (§4 — `NO`, `AU`, `EA`, `SET`, `JOB`, `kg`,
 * `meter`), plus a sentinel for "type your own". Free text remains reachable —
 * it is just no longer the default path.
 */
const UNIT_LABELS = ["NO", "AU", "EA", "SET", "JOB", "kg", "meter"]
const CUSTOM_UNIT = "__custom__"

/**
 * The §7.1 classification and the §4 pricing modes, keyed by the enum so a value
 * added to `EditorLine` fails the build here rather than rendering a row with no
 * label. Order comes from the key order, which is the order the owner reads.
 *
 * THE TRIGGER NEEDS THESE TOO. Base UI's `Select.Value` prints the RAW value when
 * it is not told how to turn one into words, and the raw values here are
 * `WORKS_CONTRACT` and `BY_WEIGHT` — database strings that mean nothing to the
 * person holding the phone. One table feeds the closed control and the open list,
 * so the two cannot say different things.
 */
const SUPPLY_NATURE_LABEL_KEY: Record<EditorLine["supplyNature"], string> = {
  WORKS_CONTRACT: "line.work",
  FABRICATED_GOODS: "line.product",
  TRADED_GOODS: "line.traded",
}
const SUPPLY_NATURES = Object.keys(SUPPLY_NATURE_LABEL_KEY) as EditorLine["supplyNature"][]

const PRICING_MODE_LABEL_KEY: Record<EditorLine["pricingMode"], string> = {
  LUMPSUM: "line.work",
  BY_PIECE: "line.pieces",
  BY_WEIGHT: "line.weightKg",
  BY_LENGTH: "line.lengthM",
}
const PRICING_MODES = Object.keys(PRICING_MODE_LABEL_KEY) as EditorLine["pricingMode"][]

/*
 * ═════════════════════════════════════════════════════════════════════════════
 * 44px ROWS, AND THIS IS NOT DECORATION.
 *
 * These three per-line pickers were native `<select>`s on purpose: on a phone the
 * operating system opens its own full-height wheel — bigger than anything drawn
 * in the page, already familiar, and reliably hittable without a 48px target
 * (§11.1). The owner asked for the in-page Select instead, for visual consistency
 * with the rest of the app, and that is his call. But the property the native
 * control was protecting is the one thing the swap must not lose, and shadcn's
 * `SelectItem` is about 28px (`py-1` around `text-sm`) — 16px short of a thumb
 * with dust on it. So the height is put back here, explicitly.
 *
 * IT MATTERS MOST ON THIS SCREEN. These are repeated once per line, so a bill
 * with eight lines is twenty-four of these rows, tapped while standing up.
 *
 * ``, NEVER `min-h-[44px]`: `styles/preferences.css` scales the root font
 * size to 18px/20px for the large-text preference precisely so padding, gaps and
 * touch targets grow together, so the rem class becomes 49.5px/55px for a viewer
 * who has turned it up while a pixel literal would stay stuck at 44. It is also
 * this codebase's house spelling — see `components/ui/questionnaire.tsx`.
 *
 * UNCONDITIONAL, not behind `pointer-coarse:`. The workshop phone is the primary
 * device, a desktop mouse loses nothing from a 44px row, and an unconditional
 * class cannot be defeated by a device that misreports its pointer type.
 *
 * Do not "tidy" any of this away.
 * ═════════════════════════════════════════════════════════════════════════════
 */
/*
 * THE TRIGGER IS DELIBERATELY NOT FORCED TALLER, and it was until
 * 2026-08-16. At 44px it stood a head above the search box and the "More
 * filters" button beside it — both `h-8` — and read as a rendering fault. The
 * OS wheel this replaced was a LIST, so the 44px minimum belongs on the ITEMS
 * (see `TOUCH_ITEM`, which the shared component now applies anyway); the
 * trigger is one control among several the same size. If a toolbar's targets
 * are ever raised, raise every control in it together.
 */

/* See `makeKeyFactory` — `adopt` on the resume path is what stops a restored
 * draft's keys colliding with newly added lines. */
const lineKeys = makeKeyFactory("L")
const nextKey = lineKeys.next

export function InvoiceEditorScreen({
  mode = "new",
}: {
  /**
   * "new" allocates the next number on save (§6); "record" is §6.1 — the bill
   * already exists in the owner's handwriting, so HE supplies the number and
   * the date off the paper, and the API moves the counter past it. One screen
   * for both because the lines, the customer, the preview and the §9.1 rules
   * are identical — two editors is how one of them ends up missing a field.
   */
  mode?: "new" | "record"
}): React.JSX.Element {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const recording = mode === "record"

  const [invoiceNumber, setInvoiceNumber] = useState("")
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [pickingCustomer, setPickingCustomer] = useState(false)
  const [pickingItemFor, setPickingItemFor] = useState<string | null>(null)

  const [state, setState] = useState<EditorState>(() => ({
    customerId: null,
    date: todayInputValue(),
    soNumber: "",
    soDate: "",
    challanNumber: "",
    challanDate: "",
    challanIds: [],
    quotationRef: "",
    quotationRefDate: "",
    attnName: "",
    notes: "",
    terms: "",
    discount: "",
    discountPercent: "",
    discountMode: "AMOUNT",
    lines: [emptyLine(nextKey())],
  }))

  const preview = useInvoicePreview(state)
  const create = useCreateInvoice(recording ? "record" : "new")

  /*
   * ?copyFrom=67 — Repeat Last Bill / Make a copy (§11.3). The source bill's
   * customer and lines arrive and fill the form ONCE; the date stays today's
   * and the S.O./challan references stay blank, because they belong to the old
   * order (see linesFromInvoice). Guarded by `copied` rather than keyed on the
   * query settling, so a refetch can never wipe lines he has started editing.
   */
  const [searchParams] = useSearchParams()
  /*
   * RESUMING OUTRANKS COPYING, and until 2026-08-16 it did not.
   *
   * `/invoices/new?draft=12&copyFrom=67` ran BOTH seed effects. Each is guarded
   * by its own one-shot latch (`copied` / `restored`), so neither stops the
   * other — whichever query settled second wrote last, and which that is depends
   * on the network. The owner resuming an unfinished bill could have watched a
   * different customer's lines land on top of his.
   *
   * `QuotationEditorScreen` has always had this guard (`editing || resuming`);
   * the invoice screen never grew one, which is the same drift the 2026-08-16
   * unification audit found in the numbering, watermark and totals triplets:
   * three screens written to resemble each other, agreeing until one of them
   * needed a fix.
   *
   * A URL carrying both is not something the app ever builds. It comes from an
   * edited address bar or a stale link, and the resumed DRAFT is the one with
   * unsaved work in it — so that is the one that wins.
   */
  const resuming = (searchParams.get("draft") ?? "") !== ""
  const copyFrom =
    recording || resuming ? undefined : (searchParams.get("copyFrom") ?? undefined)
  const source = useInvoice(copyFrom)
  const [copied, setCopied] = useState(false)

  /*
   * ?draft=12 — resume an unfinished bill (§7.2 amendment). The saved form
   * replaces the editor's state ONCE; from then on the autosave below keeps the
   * server copy current. Off entirely in record mode: a §6.1 bill already
   * exists on paper, and a server draft of it would be a second source of
   * truth for a document whose truth is the ink.
   */
  const rawDraft = recording ? null : searchParams.get("draft")
  // Truthy, not just non-null: `?draft=` with no value yields "".
  const draftParam = rawDraft === null || rawDraft === "" ? undefined : rawDraft
  const resumed = useDraft<EditorState>("INVOICE", draftParam)
  const [restored, setRestored] = useState(false)
  const resumeFailed = draftParam !== undefined && resumed.isError

  useEffect(() => {
    if (restored || resumed.data === undefined) return
    setRestored(true)
    // BEFORE the payload lands in state — see `makeKeyFactory`. The restored
    // lines carry keys from a previous page load, when the counter was higher
    // than it is now; without this the next "Add line" reuses one of them.
    lineKeys.adopt(resumed.data.payload.lines)
    /*
     * MERGED OVER THE EMPTY FORM, NOT ASSIGNED OVER IT.
     *
     * A draft's payload is whatever `EditorState` looked like ON THE DAY IT WAS
     * SAVED. Assigning it wholesale means every field added to the form
     * afterwards arrives `undefined` on an older draft — and `toApiBody` calls
     * `.trim()` on those strings, so the editor throws on its first render and
     * the owner opens yesterday's half-typed bill to a blank crashed screen with
     * his work still sitting in `document_drafts`.
     *
     * That is not hypothetical: it happened to the QUOTATION editor the moment
     * `itemNo` was added on 2026-08-15, and this file had the identical line.
     * Spreading over `current` gives any missing key the empty form's default.
     *
     * IT PROTECTS TOP-LEVEL KEYS ONLY, which is why `adoptedLines` runs beside
     * it: `lines` is replaced wholesale, so a field added to `EditorLine`
     * afterwards arrives undefined on an older draft. This note used to say no
     * such field existed yet; §15(3)(a)'s `discount` was the first, and without
     * the per-line merge it threw inside `LineCard` on the first render of a
     * resumed draft.
     */
    setState((current) => ({
      ...current,
      ...resumed.data.payload,
      lines: adoptedLines(resumed.data.payload.lines),
    }))
    if (resumed.data.payload.customerId !== null && resumed.data.customerName !== "") {
      setCustomer({
        id: resumed.data.payload.customerId,
        name: resumed.data.customerName,
      } as Customer)
    }
  }, [restored, resumed.data])

  const autosave = useDraftAutosave({
    // The mount, and with it the capability the API checks: bills are guarded by
    // `invoice.create`, quotations by `quotation.create`. Never a query flag.
    kind: "INVOICE",
    state,
    customerName: customer?.name ?? "",
    draftId: draftParam === undefined ? null : Number(draftParam),
    initialVersion: resumed.data?.version ?? 1,
    initialStatus: resumed.data?.status ?? "DRAFT",
    // Has the resumed GET actually landed? Without this the hook cannot tell
    // "version 1, a new draft" from "version 1, the real one is still in
    // flight", and a resumed draft 409s against itself on its first autosave.
    loaded: resumed.data !== undefined,
    // Not while the resumed payload is still on its way — saving the EMPTY
    // form over a real draft is the one way autosave could destroy work.
    /*
     * NOT WHILE A RESUMED PAYLOAD IS STILL IN FLIGHT — saving the EMPTY form
     * over a real draft is the one way autosave could destroy work.
     *
     * `resumeFailed` REOPENS IT. Without that clause a resume that 404s (the
     * draft was issued or discarded from the shop laptop, or the link is
     * stale) left `restored` false for ever, so autosave never switched on and
     * every keystroke after it vanished silently. There is nothing to
     * overwrite in that case — the draft does not exist — so a fresh one is
     * the right outcome, and the banner above says so.
     */
    enabled: !recording && (draftParam === undefined || restored || resumeFailed),
  })

  useEffect(() => {
    if (copied || source.data === undefined) return
    setCopied(true)
    const seed = linesFromInvoice(source.data, nextKey)
    setState((current) => ({
      ...current,
      customerId: seed.customerId,
      attnName: seed.attnName,
      /*
       * §15(3)(a) TRAVELS WHOLE, OR THE COPY IS A DIFFERENT BILL.
       *
       * `linesFromInvoice` brings each line's OWN discount across and says in
       * its own comment that "the whole-bill figure is copied once, at document
       * level, by the callers of this function". This is that caller. Without
       * this line half of §15(3)(a) came over and half did not: a bill of
       * ₹1,10,000.00 gross with ₹10,000.00 off the whole amount re-billed at
       * ₹1,29,800.00 instead of ₹1,18,000.00 — ₹11,800.00 more than the customer
       * paid last month for identical work, from the button §11.3 calls "the
       * highest-value shortcut in the whole app", with nothing on screen saying
       * a reduction had been dropped.
       *
       * COPIED ON PURPOSE, exactly as the quotation editor copies it: what is
       * being repeated is a PRICE that was agreed, and silently re-billing at
       * full rate is the mistake he would not notice. The apportioned SHARES are
       * still not copied — they are a consequence of the old document's lines
       * and the engine works them out again for the new one.
       */
      discount: discountInput(source.data.totals.documentDiscountPaise),
      /*
       * AS THE MONEY, NEVER AS THE PERCENTAGE, even when `totals
       * .documentDiscountPercentBp` says 10% is how the old bill reached it.
       * A repeat bill is priced from the AGREED figure; re-resolving "10%"
       * against lines whose rates have since moved would quietly give away a
       * different amount, from the button §11.3 calls the highest-value shortcut
       * in the app. Same rule as `linesFromInvoice`, one level up.
       */
      discountPercent: "",
      discountMode: "AMOUNT" as const,
      lines: seed.lines.length === 0 ? current.lines : seed.lines,
    }))
    setCustomer(
      source.data.customer === null
        ? null
        : ({ id: source.data.customerId, name: source.data.customer.name } as Customer),
    )
  }, [copied, source.data])

  /*
   * HYDRATE THE CHOSEN-CUSTOMER CARD. A resumed draft and a copied bill both
   * arrive with a customerId and at best a name; the card needs the whole row
   * (GSTIN, state, the CGST-vs-IGST hint). Fetch it whenever the id is known
   * but the row in hand is partial, and never overwrite a row the picker chose.
   */
  const hydrate = useCustomerById(
    state.customerId !== null && (customer === null || customer.stateCode === undefined)
      ? state.customerId
      : null,
  )
  useEffect(() => {
    if (hydrate.data === undefined) return
    setCustomer(hydrate.data)
  }, [hydrate.data])

  const patch = (changes: Partial<EditorState>) => {
    setState((current) => ({ ...current, ...changes }))
  }

  const patchLine = (key: string, changes: Partial<EditorLine>) => {
    setState((current) => ({
      ...current,
      lines: current.lines.map((line) => (line.key === key ? { ...line, ...changes } : line)),
    }))
  }

  const addLine = (parentKey: string | null = null) => {
    setState((current) => ({
      ...current,
      lines: [...current.lines, { ...emptyLine(nextKey()), parentKey }],
    }))
  }

  const addGroup = () => {
    const headerKey = nextKey()
    setState((current) => ({
      ...current,
      lines: [
        ...current.lines,
        { ...emptyLine(headerKey), isGroupHeader: true },
        { ...emptyLine(nextKey()), parentKey: headerKey },
      ],
    }))
  }

  const removeLine = (key: string) => {
    setState((current) => ({
      ...current,
      // A header takes its children with it — §4 allows two levels, and an
      // orphaned child would be a billable line with no job title above it.
      lines: current.lines.filter((line) => line.key !== key && line.parentKey !== key),
    }))
  }

  const body = toApiBody(state)
  const canSave =
    body !== null &&
    preview.data !== undefined &&
    !create.isPending &&
    // §6.1 — the number off the paper is the one field that is not optional.
    (!recording || invoiceNumber.trim() !== "")

  /*
   * PREVIEW — the unsaved form as the PDF it would become, DRAFT-watermarked
   * corner to corner by the same renderer a real bill uses (§7.1a). Opens in a
   * new tab: the editor's state must survive the look, and navigating away
   * from a half-typed bill to see it would defeat the point.
   */
  const [previewing, setPreviewing] = useState(false)
  const onPreviewPdf = async () => {
    if (body === null) return
    setPreviewing(true)
    try {
      const suffix = autosave.draftUsedId === null ? "" : `?draft=${String(autosave.draftUsedId)}`
      const fetched = await requestBlob(`${endpoints.invoicePreviewPdf}${suffix}`, {
        body,
        fallbackFilename: "draft-preview.pdf",
      })
      const url = URL.createObjectURL(fetched.blob)
      window.open(url, "_blank", "noopener")
      // A minute is enough for the tab to take the bytes; holding the object
      // URL longer pins the whole blob in memory for every preview pressed.
      window.setTimeout(() => {
        URL.revokeObjectURL(url)
      }, 60_000)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("err.somethingWentWrong"))
    } finally {
      setPreviewing(false)
    }
  }

  const onSave = () => {
    if (body === null) return
    if (recording && invoiceNumber.trim() === "") return
    const payload = recording ? { ...body, invoiceNumber: invoiceNumber.trim() } : body
    create.mutate(payload, {
      onSuccess: (result) => {
        toast.success(t("msg.saved", { defaultValue: t("status.saved") }))
        /*
         * THE WARNINGS USED TO BE THROWN AWAY HERE, and that was the worst bug
         * on this screen: the server said "this invoice will print without a
         * seller GSTIN (§9.2)" and the app answered with a success toast. The
         * bill is legally invalid and nothing on any screen ever said so.
         *
         * Toasts rather than navigation state because they survive the
         * `replace: true` navigation below, they stack when there are several,
         * and `duration: Infinity` keeps a legal-validity warning on screen
         * until he dismisses it — a 4-second auto-hide is how a warning gets
         * technically shown and never read.
         */
        for (const warning of result.warnings ?? []) {
          toast.warning(warning, { duration: Infinity, closeButton: true })
        }
        // The bill exists with a number now; the draft's job is done. Leaving
        // it would show a second, numberless copy of a real invoice.
        void autosave.completed()
        void navigate(routes.invoice(result.data.id), { replace: true })
      },
    })
  }

  return (
    <div className="flex flex-col gap-4 p-4 pb-32 md:gap-6 md:p-6 md:pb-32">
      <Link
        to={routes.invoices}
        className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1.5 text-sm"
      >
        <ArrowLeftIcon className="size-4" />
        {t("nav.invoices")}
      </Link>

      {/*
        * THE SAME HEADER SHAPE AS THE BILL SCREEN, deliberately: identity on
        * the left (title, with the DRAFT/INV badge sitting where "Issued"
        * sits, at the same badge size), actions on the right. An editor whose
        * header is laid out differently from the document it becomes reads as
        * a different app; keeping the grammar identical means dad learns it
        * once.
        */}
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {recording ? t("recordManual.title") : t("invoice.newTitle")}
            </h1>
            {!recording && autosave.draftUsedId !== null ? (
              <DraftNameBadge kind="INVOICE" id={autosave.draftUsedId} status={autosave.status} />
            ) : null}
          </div>
          {/* §6 — the number is allocated inside the save transaction so the
            * series has no gaps. There is nothing honest to show here yet.
            * In record mode the opposite is true: the number already exists in
            * ink, and the box below takes it. */}
          <p className="text-muted-foreground text-sm">
            {recording ? t("recordManual.whyTwo") : t("invoice.numberOnSave")}
          </p>
        </div>

        {recording ? null : (
          <div className="flex flex-wrap items-center gap-2" role="status">
            {autosave.saving ? (
              <Badge
                variant="outline"
                className="h-8 gap-1.5 rounded-lg px-2.5 text-sm font-medium [&>svg]:size-4!"
              >
                <Spinner data-icon="inline-start" />
                {t("draft.saving")}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">
                {autosave.savedAt !== null
                  ? t("draft.savedAt", {
                      time: autosave.savedAt.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      }),
                    })
                  : t("draft.notYet")}
              </span>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={!autosave.canSaveNow || autosave.saving}
              onClick={autosave.saveNow}
            >
              <SaveIcon />
              {t("btn.saveNow")}
            </Button>
            {/* §7.2 — the handoff: "I have checked this one, someone can issue
              * it." On the FORM, never on the bill; an invoice is DRAFT, ISSUED
              * or CANCELLED and nothing here adds to that list. */}
            <DraftReadyControl
              status={autosave.status}
              exists={autosave.draftUsedId !== null}
              pending={autosave.settingStatus}
              onChange={autosave.changeStatus}
            />
          </div>
        )}
      </header>

      {recording ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("recordManual.fromTheBookTitle")}</CardTitle>
            <p className="text-muted-foreground text-sm">{t("recordManual.fromTheBookIntro")}</p>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="record-number">{t("recordManual.numberLabel")}</Label>
              <Input
                id="record-number"
                value={invoiceNumber}
                onChange={(event) => {
                  setInvoiceNumber(event.target.value)
                }}
                placeholder="INV/26-27/67"
              />
              <p className="text-muted-foreground text-xs">{t("recordManual.numberHelp")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="record-date">{t("recordManual.dateLabel")}</Label>
              {/* The SAME state.date the ordinary editor uses — §6.1's "his
                * date, not today" is enforced by him typing it, and the API
                * checks the number's FY against it. */}
              <DateField
                id="record-date"
                value={state.date}
                onChange={(value) => {
                  patch({ date: value })
                }}
              />
              <p className="text-muted-foreground text-xs">{t("recordManual.dateHelp")}</p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* The other device won a concurrent edit (§7.2). Autosave has stopped;
        * reloading adopts the newer version. */}
      {autosave.conflict !== null ? <DraftConflictAlert kind="INVOICE" /> : null}

      {/*
        * THE RESUME FAILED, AND SAYING SO BEATS A BLANK FORM. The draft was
        * turned into a document or discarded elsewhere; autosave has been
        * reopened (see `enabled`) so typing from here starts a fresh draft
        * rather than disappearing.
        */}
      {resumeFailed ? (
        <Alert>
          <AlertTitle>{t("draft.goneTitle")}</AlertTitle>
          <AlertDescription>{t("draft.goneHelp")}</AlertDescription>
        </Alert>
      ) : null}

      {create.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotSave")}</AlertTitle>
          <AlertDescription>
            {create.error instanceof ApiError ? create.error.message : t("err.somethingWentWrong")}
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t("invoice.aboutTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>{t("lbl.customer")}</Label>
            <ChosenCustomer
              customer={customer}
              onChange={() => {
                setPickingCustomer(true)
              }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-date">{t("lbl.date")}</Label>
            <DateField
              id="invoice-date"
              value={state.date}
              onChange={(value) => {
                patch({ date: value })
              }}
            />
            {/* §8.7 — the date selects the GST rate period, and §6 the number
              * series. It is not a cosmetic field. */}
            <p className="text-muted-foreground text-xs">{t("invoice.dateHelp")}</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-attn">{t("lbl.contactName")}</Label>
            <Input
              id="invoice-attn"
              value={state.attnName}
              onChange={(event) => {
                patch({ attnName: event.target.value })
              }}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("invoice.referencesTitle")}</CardTitle>
          <CardDescription>{t("invoice.referencesHelp")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <RefField
            label={t("lbl.soNumber")}
            value={state.soNumber}
            date={state.soDate}
            onValue={(soNumber) => {
              patch({ soNumber })
            }}
            onDate={(soDate) => {
              patch({ soDate })
            }}
          />
          <RefField
            label={t("lbl.challanNumber")}
            value={state.challanNumber}
            date={state.challanDate}
            onValue={(challanNumber) => {
              patch({ challanNumber })
            }}
            onDate={(challanDate) => {
              patch({ challanDate })
            }}
          />

          {/*
            * §17.7 — OUR deliveries, directly beneath the free-text reference
            * above, so the distinction is visible rather than explained: the
            * box above is what PRINTS (most often the customer's own challan
            * number, §6), this is what the app LINKS and closes.
            *
            * It renders nothing at all until this customer actually has an
            * unbilled delivery. An empty picker on every bill would be a
            * permanent invitation to look for something that is not there.
            */}
          <BillDeliveries
            customerId={state.customerId}
            selected={state.challanIds}
            onChange={(challanIds) => {
              patch({ challanIds })
            }}
          />
          <RefField
            label={t("lbl.quotationRef")}
            value={state.quotationRef}
            date={state.quotationRefDate}
            onValue={(quotationRef) => {
              patch({ quotationRef })
            }}
            onDate={(quotationRefDate) => {
              patch({ quotationRefDate })
            }}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("line.heading")}</CardTitle>
          <CardDescription>{t("line.serverRecomputes")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {state.lines.map((line) => (
            <LineCard
              key={line.key}
              line={line}
              lines={state.lines}
              preview={preview.data?.data.lines.find((row) => row.key === line.key)}
              // §15(3)(a) applies to a tax invoice, and this is one.
              discountable
              onChange={(changes) => {
                patchLine(line.key, changes)
              }}
              onRemove={() => {
                removeLine(line.key)
              }}
              onPickItem={() => {
                setPickingItemFor(line.key)
              }}
              onAddChild={() => {
                addLine(line.key)
              }}
            />
          ))}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                addLine()
              }}
            >
              <PlusIcon />
              {t("btn.addLine")}
            </Button>
            <Button type="button" variant="outline" onClick={addGroup}>
              <ListPlusIcon />
              {t("btn.addSubLine")}
            </Button>
          </div>

          <Separator />

          <DocumentDiscountField
            value={state}
            totals={preview.data?.data.totals}
            onChange={patch}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("invoice.wordsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-notes">{t("lbl.notes")}</Label>
            <Textarea
              id="invoice-notes"
              rows={3}
              value={state.notes}
              onChange={(event) => {
                patch({ notes: event.target.value })
              }}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invoice-terms">{t("lbl.terms")}</Label>
            <Textarea
              id="invoice-terms"
              rows={3}
              value={state.terms}
              onChange={(event) => {
                patch({ terms: event.target.value })
              }}
            />
          </div>
        </CardContent>
      </Card>

      <TotalsBar
        preview={preview}
        canSave={canSave}
        saving={create.isPending}
        incomplete={body === null}
        onSave={onSave}
        previewing={previewing}
        onPreviewPdf={() => {
          void onPreviewPdf()
        }}
      />

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(picked) => {
          setCustomer(picked)
          patch({
            customerId: picked.id,
            // The customer's own contact is the default "Kind Attn to:" — the
            // owner overrides it when a particular person ordered this job.
            attnName: state.attnName === "" ? (picked.defaultContactName ?? "") : state.attnName,
            /*
             * THE TICKED DELIVERIES GO WITH THE OLD CUSTOMER (§17.7 rule 2).
             * A challan belongs to one buyer, and the server refuses to close
             * another buyer's delivery with a 409 naming the challan — so
             * carrying a selection across a customer change would fail the save
             * on a screen showing no hint of why. Cleared here, and the picker
             * below refills itself from the new customer's own list.
             */
            challanIds: picked.id === state.customerId ? state.challanIds : [],
          })
        }}
      />

      <ItemPicker
        open={pickingItemFor !== null}
        onOpenChange={(open) => {
          if (!open) setPickingItemFor(null)
        }}
        onPick={(item) => {
          if (pickingItemFor === null) return
          /*
           * The saved job fills the line, INCLUDING its HSN and GST rate. Those
           * two are the fields most easily got wrong by hand and the ones with
           * legal consequences (§4.1 requires an HSN on every line), which is
           * the whole reason the rate card exists.
           */
          patchLine(pickingItemFor, {
            itemId: item.id,
            description: item.description ?? item.name,
            // The catalogue knows whether this is work or material (§7.1);
            // the line inherits it and the toggle below can still override.
            supplyNature: item.supplyNature as EditorLine["supplyNature"],
            hsn: item.hsn,
            gstRatePercent: basisPointsToInputValue(item.gstRateBp),
            pricingMode: item.pricingMode as EditorLine["pricingMode"],
            unitLabel: item.unitLabel,
            rate: item.ratePaise === 0 ? "" : (item.ratePaise / 100).toFixed(2),
          })
          setPickingItemFor(null)
        }}
      />
    </div>
  )
}

/** A reference number and its date, which §9.2 prints as a pair. */
function RefField({
  label,
  value,
  date,
  onValue,
  onDate,
}: {
  label: string
  value: string
  date: string
  onValue: (value: string) => void
  onDate: (value: string) => void
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(event) => {
            onValue(event.target.value)
          }}
        />
        <DateField
          className="w-44"
          ariaLabel={label}
          clearable
          value={date}
          onChange={onDate}
        />
      </div>
    </div>
  )
}

/**
 * "Which deliveries does this bill close?" — §17.7's billing loop, from the
 * editor's end.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * IT IS ABSENT UNLESS THERE IS SOMETHING TO TICK, and that is the design rather
 * than an optimisation. Most bills close no delivery: an empty box captioned
 * "Deliveries to bill" on every single invoice teaches the owner to scroll past
 * this part of the screen, and the one week he actually has three unbilled
 * challans it will be the part he scrolls past. Nothing renders until this
 * customer really has an open delivery — so when the box IS there, it is there
 * because it has an answer.
 *
 * FOUR CONDITIONS, EACH HIDING IT COMPLETELY:
 *
 *   1. NO CUSTOMER YET. The eligible set is "OPEN deliveries OF THIS BUYER"
 *      (§17.7 rule 2), so there is no honest list to show before one is chosen.
 *      The query does not even run.
 *   2. NO `challan.view`. §2.1 — capabilities, never roles. The ACCOUNTANT row
 *      is not switched on yet, and the day it is, this must not be the control
 *      that leaks the register.
 *   3. THE MODULE IS OFF. Every challan route answers 404 behind
 *      `requireFlag("challans")` (§18.3), the query errors, and there are no
 *      rows — which is the same empty state as "nothing to bill" and needs no
 *      branch of its own. The server refuses a posted `challanIds` with a 400
 *      while the flag is off, so nothing can be sent from a stale screen either.
 *   4. NOTHING IS WAITING. The ordinary case.
 *
 * THE NUMBER, THE DAY AND THE VALUE, because that is how a delivery is
 * recognised. "CHL/26-27/12" alone is a string the owner has never memorised;
 * the date and the amount are what let him say "yes, that is the load that went
 * to Bhosari on the 14th". The amount is the Rule 55(1) DECLARED VALUE of goods
 * that moved — not money owed, and the caption says so, because §17.7 is
 * explicit that a challan's figure "is not money anyone owes".
 *
 * A TICK IS A LINK, NOT A TOTAL. Nothing here changes what the bill is worth:
 * the lines are the lines. The server sets `linked_invoice_id`, moves the
 * challan to INVOICED and copies its number into the printed reference, all
 * inside the transaction that allocates the invoice number — so a save that
 * never commits closes no delivery.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function BillDeliveries({
  customerId,
  selected,
  onChange,
}: {
  customerId: number | null
  /** Ids, in the order they were ticked. Sent as `challanIds` on save. */
  selected: number[]
  onChange: (next: number[]) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const { can } = useAuth()
  const allowed = can("challan.view")
  const formatDay = useFormatDay()

  // `null` when the user may not read challans: no capability, no request. The
  // server would refuse it anyway; asking would only be a refusal in the log.
  const challans = useBillableChallans(allowed ? customerId : null)
  /*
   * `Array.isArray`, NOT `?? []`. The envelope key here is `data`, while
   * `/invoices` answers `invoices` and `/items` answers `items` — the API is
   * deliberately not uniform (see lib/endpoints.ts) — so a response that is not
   * this endpoint's shape must degrade to "I know of no deliveries" rather than
   * throw `.map of undefined` inside a render and unmount a half-typed bill.
   */
  const rows: ChallanListRow[] = Array.isArray(challans.data?.data) ? challans.data.data : []

  /*
   * A RESUMED DRAFT CAN NAME A DELIVERY THAT HAS SINCE BEEN BILLED.
   *
   * §7.2 drafts survive for weeks; the challan ticked into one on Monday may be
   * closed by a bill raised from the office laptop on Wednesday. The refetched
   * list is the authority on what is still open, so an id it no longer offers
   * is dropped here rather than carried into a save that would 409 naming a
   * challan the owner cannot see on the screen in front of him.
   *
   * ONLY WHEN THE LIST ACTUALLY LOADED. A failed or in-flight query has no
   * opinion, and treating "I could not ask" as "it is gone" would silently
   * unpick a correct selection on one bad connection.
   */
  useEffect(() => {
    const answered = challans.data?.data
    if (!Array.isArray(answered)) return
    const offered = new Set(answered.map((row) => row.id))
    const kept = selected.filter((id) => offered.has(id))
    if (kept.length !== selected.length) onChange(kept)
  }, [challans.data, selected, onChange])

  if (!allowed || rows.length === 0) return null

  return (
    <div className="flex flex-col gap-1.5 sm:col-span-2">
      <Label>{t("invoice.deliveries.title")}</Label>
      <div className="flex flex-col gap-1 rounded-md border p-2">
        {rows.map((row) => {
          const checked = selected.includes(row.id)
          return (
            <div
              key={row.id}
              className="flex min-h-11 items-center gap-3 rounded-md px-2 py-1.5"
            >
              <Checkbox
                /*
                 * THE WHOLE ROW IN ONE STRING, because a screen reader reaching
                 * a bare "checkbox" here would be asked to close a delivery it
                 * cannot name. It is also what the tests tick by.
                 */
                aria-label={t("invoice.deliveries.pick", {
                  number: row.number,
                  date: formatDay(row.date),
                  amount: formatRupees(row.grandTotalPaise),
                })}
                checked={checked}
                onCheckedChange={(next) => {
                  onChange(
                    next === true
                      ? [...selected, row.id]
                      : selected.filter((id) => id !== row.id),
                  )
                }}
              />
              <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
                <span className="font-medium tabular-nums">{row.number}</span>
                <span className="text-muted-foreground text-sm">{formatDay(row.date)}</span>
              </span>
              {/* Through `formatRupees`, never `toLocaleString` — Indian digit
                * grouping is correctness here, not decoration (CLAUDE.md). */}
              <span className="shrink-0 text-sm tabular-nums">
                {formatRupees(row.grandTotalPaise)}
              </span>
            </div>
          )
        })}
      </div>
      <p className="text-muted-foreground text-xs">{t("invoice.deliveries.help")}</p>
    </div>
  )
}

/**
 * One line.
 *
 * THE AMOUNT SHOWN IS THE SERVER'S, read from the matching preview line by
 * `key`. It is not `rate × qty` computed here — for BY_WEIGHT and BY_LENGTH
 * lines the taxable value comes from the measurement column, not from `qty`, and
 * reimplementing that rule is how a 12.5 kg job priced per kilo printed
 * "1.000 kg" beside an amount computed from 12.5 (services/pdf/theme.ts).
 */
export function LineCard({
  line,
  lines,
  preview,
  discountable,
  onChange,
  onRemove,
  onPickItem,
  onAddChild,
}: {
  line: EditorLine
  lines: EditorLine[]
  /**
   * THE SERVER'S ANSWER FOR THIS LINE. `apportionedDiscountPaise` in particular
   * cannot be worked out here: the whole-bill discount is split across the lines
   * in proportion to their taxable values, which rounds, and the remainders are
   * handed out by largest remainder inside `@steelbill/tax` (§15(3)(a)).
   */
  preview:
    | {
        taxablePaise: number
        lineCode: string
        apportionedDiscountPaise?: number
        /**
         * §15(3)(a) — what the server took off THIS line, and the percentage it
         * says it used. The first is what "10%" actually comes to and is the
         * only place that figure exists; the second earns the `(10%)` bracket,
         * and only when it still matches what is in the box (`bracketPercentBp`).
         */
        discountPaise?: number
        discountPercentBp?: number | null
      }
    | undefined
  /**
   * May THIS document kind carry a §15(3)(a) discount? Invoices and quotations
   * can; challans and credit notes cannot.
   *
   * ═════════════════════════════════════════════════════════════════════════
   * REQUIRED, WITH NO DEFAULT, AND THAT IS THE POINT.
   *
   * This card is shared by all four editors. When the discount box was added it
   * appeared on all four — including the two whose request schemas
   * (`ChallanCreateSchema`, `CreditNoteCreateSchema`, both `LinesSchema`) STRIP
   * `discountPaise` as an unknown key. The box accepted a number, showed no
   * error, and changed nothing on the saved document: on the challan the
   * preview came back with the same taxable value it had before the keystroke,
   * and on the credit note the client's own engine applied the discount while
   * the server did not, so the screen and the issued note disagreed by the
   * discount and its tax.
   *
   * A default of `true` would have let both of those through unchanged, which
   * is how they got there. Making the prop required means the fifth editor
   * cannot be written without answering the question.
   * ═════════════════════════════════════════════════════════════════════════
   */
  discountable: boolean
  onChange: (changes: Partial<EditorLine>) => void
  onRemove: () => void
  onPickItem: () => void
  onAddChild: () => void
}): React.JSX.Element {
  const { t } = useTranslation()
  const isChild = line.parentKey !== null
  const hasChildren = lines.some((other) => other.parentKey === line.key)

  return (
    <div className={`flex flex-col gap-3 rounded-md border p-3 ${isChild ? "ml-4 sm:ml-8" : ""}`}>
      <div className="flex items-start gap-2">
        <span className="text-muted-foreground w-10 shrink-0 pt-2 text-xs tabular-nums">
          {preview?.lineCode ?? (line.isGroupHeader ? "—" : "")}
        </span>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          {/*
            * A TEXTAREA, NOT AN INPUT (§11.1). Real line text — "P/F of MS
            * sheet to the Electric panel shed with old Trolley modification" —
            * has to wrap to be readable, and the phone keyboard's microphone
            * types multi-sentence descriptions that a single-line box turns
            * into an unscrollable ribbon. One visible row until it grows, so a
            * short line costs no height.
            */}
          <Textarea
            aria-label={t("lbl.description")}
            placeholder={t("line.descriptionPlaceholder")}
            value={line.description}
            rows={1}
            className="min-h-9 resize-y"
            onChange={(event) => {
              onChange({ description: event.target.value })
            }}
          />
          {line.isGroupHeader ? (
            // §4 — a header's rate is DERIVED from its children by the engine.
            // There is nothing to type here, and typing one was the real defect
            // on invoice #66.
            <p className="text-muted-foreground text-xs">{t("line.derived")}</p>
          ) : null}
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t("line.removeLine", { code: preview?.lineCode ?? "" })}
          onClick={onRemove}
        >
          <Trash2Icon className="size-4" />
        </Button>
      </div>

      {line.isGroupHeader ? (
        <div className="flex flex-wrap gap-2 pl-12">
          <Button type="button" variant="outline" onClick={onAddChild}>
            <PlusIcon />
            {t("line.addSubLineCustom")}
          </Button>
        </div>
      ) : (
        <>
          <div className="grid gap-3 pl-12 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <Label>HSN/SAC</Label>
              <Input
                value={line.hsn}
                inputMode="numeric"
                maxLength={8}
                className="tabular-nums"
                onChange={(event) => {
                  onChange({ hsn: event.target.value })
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t("line.gstRate")}</Label>
              <Input
                value={line.gstRatePercent}
                inputMode="decimal"
                className="tabular-nums"
                onChange={(event) => {
                  onChange({ gstRatePercent: event.target.value })
                }}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t("lbl.quantity")}</Label>
              <div className="flex gap-2">
                <Input
                  value={line.qty}
                  inputMode="decimal"
                  className="tabular-nums"
                  onChange={(event) => {
                    onChange({ qty: event.target.value })
                  }}
                />
                {/*
                  * §11.1 — a dropdown of the units the real documents use, with
                  * "type your own" as the escape hatch rather than the default.
                  * Free text was how "NO", "no", "Nos" and "N0" all ended up in
                  * one register.
                  */}
                {UNIT_LABELS.includes(line.unitLabel) ? (
                  /*
                   * NO `itemToStringLabel` HERE, AND THAT IS DELIBERATE: every
                   * unit's label IS its value ("kg" prints as "kg"), so Base UI's
                   * default stringification of the value is already the right
                   * text on the trigger. The one exception, `CUSTOM_UNIT`, is
                   * never stored — see the sentinel note on `onValueChange` — so
                   * it can never reach the trigger.
                   */
                  <Select
                    value={line.unitLabel}
                    onValueChange={(unit) => {
                      /*
                       * THE SENTINEL STORES `""`, WHICH IS NOT ANY OPTION'S VALUE
                       * — and that is exactly what swaps this control for the
                       * free-text Input beside it. Do NOT read this as the filter
                       * screens' `"" means no filter` idiom; it is the inversion
                       * of it. `null` is folded in with the sentinel because
                       * `onValueChange` is typed to allow it and free text is the
                       * safe landing place for a value this list does not hold.
                       */
                      onChange({
                        unitLabel: unit === CUSTOM_UNIT || unit === null ? "" : unit,
                      })
                    }}
                  >
                    {/* The `aria-label` moves onto the TRIGGER: Base UI's Root
                      * renders no element of its own, so an aria-label on it goes
                      * nowhere. There is no `<Label htmlFor>` competing with it. */}
                    <SelectTrigger
                      aria-label={t("lbl.unit")}
                      className="w-24"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_LABELS.map((unit) => (
                        <SelectItem key={unit} value={unit}>
                          {unit}
                        </SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_UNIT}>
                        {t("line.customUnit")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    aria-label={t("line.customUnitLabel")}
                    className="w-24"
                    autoFocus={line.unitLabel === ""}
                    placeholder={t("line.customUnitLabel")}
                    value={line.unitLabel}
                    onChange={(event) => {
                      onChange({ unitLabel: event.target.value })
                    }}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t("line.workKind")}</Label>
              {/*
                * §7.1 — snapshot onto the line at save. Before this control the
                * editor sent nothing and every custom line was stored as a
                * works contract; the tax was still right (HSN and rate are
                * typed independently), only the classification was wrong.
                */}
              <Select
                value={line.supplyNature}
                itemToStringLabel={(nature) => t(SUPPLY_NATURE_LABEL_KEY[nature])}
                onValueChange={(nature) => {
                  // Typed to allow null; there is no empty option and a line
                  // always carries a classification, so nothing is the honest
                  // response rather than writing a null onto the line.
                  if (nature === null) return
                  onChange({ supplyNature: nature })
                }}
              >
                {/* `w-full`, where the native control was sized by its widest
                  * option: a `w-fit` trigger is only as wide as the CURRENT label,
                  * so the box would jump between "Work" and "Bought-in material"
                  * as the answer changed. */}
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SUPPLY_NATURES.map((nature) => (
                    <SelectItem key={nature} value={nature}>
                      {t(SUPPLY_NATURE_LABEL_KEY[nature])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label>{t("lbl.rate")}</Label>
              <Input
                value={line.rate}
                inputMode="decimal"
                className="tabular-nums"
                placeholder="0.00"
                onChange={(event) => {
                  onChange({ rate: event.target.value })
                }}
              />
            </div>

            {/*
              * §15(3)(a) — a discount on THIS line, taken BEFORE tax, given
              * either in rupees or as a percentage of the line's gross.
              *
              * NOTHING IS SUBTRACTED HERE AND NO PERCENTAGE IS RESOLVED HERE.
              * The AMOUNT below is the server's figure, already net; the reduced
              * taxable value appears as he types because the preview refires,
              * not because this screen did any arithmetic (§6, CLAUDE.md).
              *
              * ABSENT ENTIRELY on a challan and on a credit note — see
              * `discountable`. Not disabled and not hidden with the value still
              * in state: an unreachable field that still posts is the same
              * silent-strip defect one layer down.
              */}
            {discountable ? (
              <LineDiscountField
                line={line}
                lineKey={line.key}
                preview={preview}
                onChange={onChange}
              />
            ) : null}
          </div>

          <div className="grid gap-3 pl-12 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <Label>{t("line.workKind")}</Label>
              <Select
                value={line.pricingMode}
                itemToStringLabel={(mode) => t(PRICING_MODE_LABEL_KEY[mode])}
                onValueChange={(mode) => {
                  // SYNCHRONOUS, AND THE SAME SHAPE AS BEFORE. This choice decides
                  // which `MeasureField` renders below it, and the engine takes the
                  // taxable value from that column rather than from `qty`.
                  if (mode === null) return
                  onChange({ pricingMode: mode })
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_MODES.map((mode) => (
                    <SelectItem key={mode} value={mode}>
                      {t(PRICING_MODE_LABEL_KEY[mode])}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/*
              * The measurement the pricing mode requires. The schema refuses the
              * line without it, and the ENGINE takes the taxable value from this
              * column rather than from `qty` — so RATE × this is the amount.
              */}
            {line.pricingMode === "BY_PIECE" ? (
              <MeasureField
                label={t("line.pieces")}
                value={line.pieces}
                onChange={(pieces) => {
                  onChange({ pieces })
                }}
              />
            ) : null}
            {line.pricingMode === "BY_WEIGHT" ? (
              <MeasureField
                label={t("line.weightKg")}
                value={line.weightKg}
                onChange={(weightKg) => {
                  onChange({ weightKg })
                }}
              />
            ) : null}
            {line.pricingMode === "BY_LENGTH" ? (
              <MeasureField
                label={t("line.lengthM")}
                value={line.lengthM}
                onChange={(lengthM) => {
                  onChange({ lengthM })
                }}
              />
            ) : null}

            <div className="flex items-end">
              <Button type="button" variant="outline" onClick={onPickItem}>
                {t("btn.fromMyList")}
              </Button>
            </div>

            <div className="flex flex-col items-end justify-end">
              <span className="text-muted-foreground text-xs">
                {/* "Taxable value" once anything has come off, because that is
                  * what the figure now is and what the GST below it is charged
                  * on (§15(3)(a)). Plain "Amount" while they agree. */}
                {discountable &&
                ((preview?.apportionedDiscountPaise ?? 0) > 0 || typedDiscount(line) !== "")
                  ? t("line.taxableValue")
                  : t("lbl.amount")}
              </span>
              <span className="font-medium tabular-nums">
                {/* THE SERVER'S NUMBER. Never rate × qty computed here, and
                  * never rate × qty minus the discount either — it is already
                  * net of this line's own discount AND of its share of the
                  * whole-bill one. */}
                {preview === undefined ? "—" : formatRupees(preview.taxablePaise)}
              </span>
              {/* The share of the whole-bill discount that landed on this line.
                * Shown because the AMOUNT above is otherwise unexplainable on a
                * line the owner typed no discount against. */}
              {(preview?.apportionedDiscountPaise ?? 0) > 0 ? (
                <span className="text-muted-foreground text-xs tabular-nums">
                  {t("line.discountShare", {
                    amount: formatRupees(preview?.apportionedDiscountPaise ?? 0),
                  })}
                </span>
              ) : null}
            </div>
          </div>
        </>
      )}

      {hasChildren ? <Separator /> : null}
    </div>
  )
}

function MeasureField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <Input
        value={value}
        inputMode="decimal"
        className="tabular-nums"
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
    </div>
  )
}

/**
 * The running total, pinned to the bottom of the screen.
 *
 * PINNED BECAUSE THE OWNER SCROLLS. On a phone the lines push the total off the
 * screen exactly when he is deciding whether the bill is right, and "what does
 * this come to?" is the question he is asking while he types. Every figure here
 * is the server's.
 *
 * SHARED WITH THE QUOTATION EDITOR, which is why the save label and the
 * emphasised figure are props rather than literals — under §9.3's TAX_EXTRA a
 * quotation's headline number is the PRE-TAX subtotal, and a bar that always
 * shouted the tax-inclusive total would contradict the sheet being written.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * AND SHARED WITH THE CREDIT-NOTE EDITOR SINCE 2026-08-16, WHICH IS WHY THE
 * `preview` PROP IS NO LONGER A QUERY RESULT.
 *
 * It used to be typed `ReturnType<typeof useInvoicePreview>` — the whole
 * TanStack query object — for the three editors that call `POST /…/preview`.
 * §17.3's credit note has NO preview route (see the header of
 * `features/credit-notes/use-credit-note.ts`): its figures come from
 * `useLiveTotals`, the same `@steelbill/tax` engine, run in the browser. A bar
 * that demanded a query object would have forced a FOURTH totals bar into
 * existence beside this one, and the two would have drifted the first time
 * either was touched — which is exactly the fourth-branch failure §17.3's brief
 * names.
 *
 * So the prop is now the SMALLEST SHAPE THIS COMPONENT ACTUALLY READS. A
 * `UseQueryResult<PreviewEnvelope<DocumentPreview>>` satisfies it structurally,
 * so the three existing call sites are unchanged and pass their query straight
 * through; the credit-note editor hands over a plain object it builds itself.
 * Nothing here knows or cares which it was given, and no figure moved.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export interface TotalsBarFigures {
  /**
   * The computed document, in the preview envelope's shape. `undefined` while
   * the lines are not yet computable — the bar then says so rather than
   * printing a zero, which on a totals bar reads as a bill for nothing.
   */
  data?:
    | {
        data: { supplyType: string; totals: DocumentTotals }
        /** Advisory sentences from the server. Amber, never swallowed. */
        warnings?: string[]
      }
    | undefined
  isError: boolean
  /** `unknown` because it may be an `ApiError` or a local `Error`; the bar narrows. */
  error: unknown
}

export function TotalsBar({
  preview,
  canSave,
  saving,
  incomplete,
  onSave,
  previewing,
  onPreviewPdf,
  saveLabel,
  /**
   * §9.3 — show the subtotal as the headline instead of the grand total, with
   * the tax-inclusive figure demoted beneath it. Off for every invoice: a tax
   * invoice's total IS the tax-inclusive one.
   */
  emphasiseSubtotal = false,
}: {
  preview: TotalsBarFigures
  canSave: boolean
  saving: boolean
  incomplete: boolean
  onSave: () => void
  previewing?: boolean
  /**
   * OMITTED HIDES THE BUTTON — and as of 2026-08-16 all three editors pass it.
   *
   * This note used to say the quotation editor omitted it because the API had
   * `/invoices/preview-pdf` and no counterpart. `POST /quotations/preview-pdf` and
   * `POST /challans/preview-pdf` now exist (in `apps/api/src/routes/documents-pdf.ts`,
   * which is why the claim went stale unnoticed — readers checked the per-kind
   * routers). The prop stays optional rather than required: hiding remains the
   * honest answer for any future editor whose kind the API cannot render.
   */
  onPreviewPdf?: () => void
  saveLabel?: string
  emphasiseSubtotal?: boolean
}): React.JSX.Element {
  const { t } = useTranslation()
  const data = preview.data?.data
  const warnings = preview.data?.warnings ?? []

  return (
    <div className="bg-background/95 fixed inset-x-0 bottom-0 z-20 border-t backdrop-blur md:left-[var(--sidebar-width,0px)]">
      <div className="mx-auto flex max-w-5xl flex-col gap-2 p-3 md:p-4">
        {preview.isError ? (
          <p className="text-destructive text-xs">
            {preview.error instanceof ApiError
              ? preview.error.message
              : t("err.somethingWentWrong")}
          </p>
        ) : null}

        {warnings.map((warning) => (
          /*
           * Amber, not muted grey. These are the server saying the document has
           * a real problem — a missing seller GSTIN makes the bill legally
           * invalid (§9.2) — and grey-on-grey under the totals is how that gets
           * scrolled past. Same tone the DRAFT badge uses: attention, not error.
           */
          <p key={warning} className="text-xs font-medium text-amber-600 dark:text-amber-500">
            {warning}
          </p>
        ))}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col">
            {data === undefined ? (
              <span className="text-muted-foreground text-sm">
                {incomplete ? t("line.totalIncomplete") : t("preview.checking")}
              </span>
            ) : (
              <>
                {/*
                  * §15(3)(a) ON THE BAR HE IS ACTUALLY WATCHING. The subtotal
                  * beside it is already NET, so without this row a ₹1,000
                  * discount is invisible on the one figure the owner checks
                  * before he saves — and the sheet would then show a reduction
                  * the screen never mentioned. Only when there IS one.
                  */}
                {data.totals.totalDiscountPaise > 0 ? (
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {t("doc.beforeDiscount")} {formatRupees(data.totals.grossSubtotalPaise)}
                    {" · "}
                    {t("doc.lessDiscount")} {formatRupees(data.totals.totalDiscountPaise)}
                  </span>
                ) : null}
                <span className="text-muted-foreground text-xs tabular-nums">
                  {t("lbl.subtotal")} {formatRupees(data.totals.subtotalPaise)}
                  {" · "}
                  {data.supplyType === "INTER_STATE"
                    ? `${t("preview.igst")} ${formatRupees(data.totals.igstPaise)}`
                    : `${t("preview.cgst")} ${formatRupees(data.totals.cgstPaise)} · ${t("preview.sgst")} ${formatRupees(data.totals.sgstPaise)}`}
                </span>
                <span className="text-lg font-semibold tabular-nums">
                  {formatRupees(
                    emphasiseSubtotal
                      ? data.totals.subtotalPaise
                      : data.totals.grandTotalPaise,
                  )}
                </span>
                {/* The other figure, demoted — the owner still needs to know
                  * what the job will actually invoice at (§9.3). */}
                {emphasiseSubtotal ? (
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {t("quotation.withTaxLabel")}{" "}
                    {formatRupees(data.totals.grandTotalPaise)}
                  </span>
                ) : null}
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/*
              * PREVIEW BEFORE COMMITTING. Saving is irreversible — the number
              * is spent the moment it commits (§6) — so the last look belongs
              * right next to the button that spends it. Same `lg` size: the
              * pair must read as two doors, not a door and a decoration. The
              * sheet opens DRAFT-watermarked in a new tab; this form survives.
              */}
            {onPreviewPdf === undefined ? null : (
              <Button
                size="lg"
                variant="outline"
                disabled={!canSave || previewing === true}
                onClick={onPreviewPdf}
              >
                {previewing === true ? <Loader2Icon className="animate-spin" /> : <EyeIcon />}
                {t("btn.previewPdf")}
              </Button>
            )}
            <Button size="lg" disabled={!canSave} onClick={onSave}>
            {saving ? <Loader2Icon className="animate-spin" /> : null}
            {saveLabel ?? t("btn.makeTheBill")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
